import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import { ArrowBack, Edit, Send, CheckCircle, HighlightOff, Description } from '@mui/icons-material';
import {
  PageHeader,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  StatusTone,
  TableEmptyState,
} from '../../components/common/pageKit';
import { formatIndianNumber } from '../../utils/currency';
import { procurementService, ServiceEntryDetail, ServiceEntryStatus } from '../../services/procurementService';

const STATUS_TONE: Record<ServiceEntryStatus, StatusTone> = {
  DRAFT: 'neutral',
  SUBMITTED: 'info',
  APPROVED: 'success',
  REJECTED: 'error',
  CANCELLED: 'neutral',
};

const toNum = (v: number | string | null | undefined): number => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

const fmtDate = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('en-IN') : '—');

const ServiceEntryDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [entry, setEntry] = useState<ServiceEntryDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const { serviceEntry } = await procurementService.getServiceEntry(id);
      setEntry(serviceEntry);
    } catch (err: any) {
      setError(err.message || 'Failed to load service entry sheet');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !entry) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/procurement/service-entries')} sx={{ mb: 2 }}>
          Back to Service Entries
        </Button>
        <Alert severity="error">{error || 'Service entry sheet not found'}</Alert>
      </Box>
    );
  }

  const canEdit = entry.status === 'DRAFT';
  const canSubmit = entry.status === 'DRAFT';
  const canApproveReject = entry.status === 'SUBMITTED';
  const noActionsAvailable = !canEdit && !canSubmit && !canApproveReject;

  const total = entry.lines.reduce((sum, l) => sum + toNum(l.amount ?? toNum(l.quantity) * toNum(l.rate)), 0);

  const runAction = async (name: string, fn: () => Promise<void>) => {
    setActionLoading(name);
    setActionError(null);
    try {
      await fn();
    } catch (err: any) {
      setActionError(err.message || `Failed to ${name}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSubmit = () =>
    runAction('submit', async () => {
      await procurementService.submitServiceEntry(id!);
      setNotice('Service entry sheet submitted for approval.');
      await load();
    });

  const handleApprove = () =>
    runAction('approve', async () => {
      await procurementService.approveServiceEntry(id!);
      setNotice('Service entry sheet approved.');
      await load();
    });

  const handleReject = () =>
    runAction('reject', async () => {
      await procurementService.rejectServiceEntry(id!, rejectReason.trim());
      setRejectOpen(false);
      setRejectReason('');
      setNotice('Service entry sheet rejected.');
      await load();
    });

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/procurement/service-entries')} sx={{ mb: 1 }}>
        Back to Service Entries
      </Button>

      <PageHeader
        title={entry.ses_number}
        qualifier={entry.vendor_name ? `· ${entry.vendor_name}` : undefined}
        subtitle={`Against PO ${entry.po_number} · ${fmtDate(entry.service_period_from)} – ${fmtDate(entry.service_period_to)}`}
        actions={<StatusDot label={entry.status} tone={STATUS_TONE[entry.status] || 'neutral'} />}
      />

      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
          Workflow Actions
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
          {canEdit && (
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={() => navigate(`/procurement/service-entries/${id}/edit`)}
            >
              Edit
            </Button>
          )}
          {canSubmit && (
            <Button variant="contained" startIcon={<Send />} disabled={actionLoading !== null} onClick={handleSubmit}>
              {actionLoading === 'submit' ? 'Submitting...' : 'Submit for Approval'}
            </Button>
          )}
          {canApproveReject && (
            <>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircle />}
                disabled={actionLoading !== null}
                onClick={handleApprove}
              >
                {actionLoading === 'approve' ? 'Approving...' : 'Approve'}
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<HighlightOff />}
                disabled={actionLoading !== null}
                onClick={() => setRejectOpen(true)}
              >
                Reject
              </Button>
            </>
          )}
          {noActionsAvailable && (
            <Typography variant="body2" color="text.secondary">
              This service entry sheet is {entry.status.toLowerCase()} -- no further actions are available.
            </Typography>
          )}
        </Box>
      </Paper>

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Service Entry Details
            </Typography>
            <Grid container spacing={1.5}>
              {[
                ['Purchase Order', entry.po_number],
                ['Vendor', entry.vendor_name],
                ['Service Period', `${fmtDate(entry.service_period_from)} – ${fmtDate(entry.service_period_to)}`],
                ['Description', entry.description],
              ].map(([label, value]) => (
                <Grid size={{ xs: 6, sm: 4 }} key={label}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {value || '—'}
                  </Typography>
                </Grid>
              ))}
            </Grid>
            {entry.remarks && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Remarks
                </Typography>
                <Typography variant="body2">{entry.remarks}</Typography>
              </>
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Total
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Amount</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{formatIndianNumber(total)}</Typography>
            </Box>
            <Box sx={{ mt: 2 }}>
              <Button
                size="small"
                startIcon={<Description />}
                onClick={() => navigate(`/procurement/orders/${entry.po_id}`)}
              >
                View Purchase Order {entry.po_number}
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <TableSurface>
            <Box sx={{ p: 2.5, pb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Service Lines
              </Typography>
            </Box>
            <Table>
              <TableHead>
                <TableRow>
                  {['Description', 'Qty / Hours', 'UOM', 'Rate', 'Amount'].map((h, i) => (
                    <TableCell key={h} align={i >= 1 && i !== 2 ? 'right' : 'left'} sx={headerCellSx}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {entry.lines.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ border: 0 }}>
                      <TableEmptyState title="No service lines on this entry sheet" />
                    </TableCell>
                  </TableRow>
                ) : (
                  entry.lines.map((l) => (
                    <TableRow key={l.id} sx={bodyRowSx}>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {l.description}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {toNum(l.quantity)}
                      </TableCell>
                      <TableCell>{l.uom || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatIndianNumber(toNum(l.rate))}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                        {formatIndianNumber(toNum(l.amount ?? toNum(l.quantity) * toNum(l.rate)))}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableSurface>
        </Grid>
      </Grid>

      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject Service Entry Sheet</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            The reason will be visible on the service entry sheet.
          </DialogContentText>
          <TextField
            label="Rejection Reason"
            fullWidth
            multiline
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={rejectReason.trim().length < 3 || actionLoading === 'reject'}
          >
            {actionLoading === 'reject' ? 'Rejecting...' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServiceEntryDetailPage;
